import type {
  BlogSEOConfig,
  BlogPostSEOProps,
  BlogPostFAQItem,
  HowToData,
  SEOAuthor,
} from './types'

/**
 * Generate BlogPosting JSON-LD schema
 * When no author is provided, uses the publisher (Organization) as the author
 */
export function generateBlogPostingJsonLd(
  post: BlogPostSEOProps['post'],
  config: BlogSEOConfig
): Record<string, unknown> {
  const blogPath = config.blogPath ?? '/blog'
  const postUrl = `${config.baseUrl}${blogPath}/${post.slug}`
  const imageUrl = post.image
    ? post.image.startsWith('http')
      ? post.image
      : `${config.baseUrl}${post.image}`
    : config.defaultOgImage
      ? `${config.baseUrl}${config.defaultOgImage}`
      : undefined

  // Build author - default to publisher (Organization) when no author provided
  const author = post.author
    ? buildPersonAuthor(post.author, config.baseUrl)
    : buildOrganizationAuthor(config)

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    url: postUrl,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': postUrl,
    },
    author,
    publisher: {
      '@type': 'Organization',
      name: config.publisher.name,
      ...(config.publisher.url && { url: config.publisher.url }),
      ...(config.publisher.logo && {
        logo: {
          '@type': 'ImageObject',
          url: config.publisher.logo.startsWith('http')
            ? config.publisher.logo
            : `${config.baseUrl}${config.publisher.logo}`,
        },
      }),
    },
  }

  if (post.description) {
    schema.description = post.description
  }

  if (imageUrl) {
    schema.image = {
      '@type': 'ImageObject',
      url: imageUrl,
    }
  }

  if (post.date) {
    schema.datePublished = post.date
  }

  if (post.dateModified) {
    schema.dateModified = post.dateModified
  } else if (post.date) {
    // Fall back to publish date if no modified date
    schema.dateModified = post.date
  }

  if (post.tags && post.tags.length > 0) {
    schema.keywords = post.tags.join(', ')
  }

  if (post.category) {
    schema.articleSection = post.category
  }

  if (post.wordCount) {
    schema.wordCount = post.wordCount
  }

  return schema
}

/**
 * Build Person author schema
 */
function buildPersonAuthor(
  author: SEOAuthor,
  baseUrl: string
): Record<string, unknown> {
  const personSchema: Record<string, unknown> = {
    '@type': 'Person',
    name: author.name,
  }

  if (author.url) {
    personSchema.url = author.url.startsWith('http')
      ? author.url
      : `${baseUrl}${author.url}`
  }

  if (author.image) {
    personSchema.image = author.image.startsWith('http')
      ? author.image
      : `${baseUrl}${author.image}`
  }

  return personSchema
}

/**
 * Build Organization author schema (used when no author provided)
 */
function buildOrganizationAuthor(
  config: BlogSEOConfig
): Record<string, unknown> {
  const orgSchema: Record<string, unknown> = {
    '@type': 'Organization',
    name: config.publisher.name,
  }

  if (config.publisher.url) {
    orgSchema.url = config.publisher.url
  }

  if (config.publisher.logo) {
    orgSchema.logo = config.publisher.logo.startsWith('http')
      ? config.publisher.logo
      : `${config.baseUrl}${config.publisher.logo}`
  }

  return orgSchema
}

/**
 * Generate FAQPage JSON-LD schema
 */
export function generateFAQJsonLd(items: BlogPostFAQItem[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }
}

/**
 * Generate HowTo JSON-LD schema
 */
export function generateHowToJsonLd(
  howTo: HowToData,
  baseUrl?: string
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: howTo.name,
    step: howTo.steps.map((step, index) => {
      const stepSchema: Record<string, unknown> = {
        '@type': 'HowToStep',
        position: index + 1,
        name: step.name,
        text: step.text,
      }

      if (step.image) {
        stepSchema.image = step.image.startsWith('http')
          ? step.image
          : baseUrl
            ? `${baseUrl}${step.image}`
            : step.image
      }

      return stepSchema
    }),
  }

  if (howTo.description) {
    schema.description = howTo.description
  }

  if (howTo.totalTime) {
    schema.totalTime = howTo.totalTime
  }

  return schema
}

/**
 * Generate BreadcrumbList JSON-LD schema
 */
export function generateBreadcrumbJsonLd(
  post: { slug: string; title: string; category?: string },
  config: BlogSEOConfig
): Record<string, unknown> {
  const blogPath = config.blogPath ?? '/blog'
  const items: Array<{
    '@type': string
    position: number
    name: string
    item: string
  }> = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: config.baseUrl,
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Blog',
      item: `${config.baseUrl}${blogPath}`,
    },
  ]

  // Add category if present
  if (post.category) {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: post.category,
      item: `${config.baseUrl}${blogPath}?category=${encodeURIComponent(post.category)}`,
    })
    items.push({
      '@type': 'ListItem',
      position: 4,
      name: post.title,
      item: `${config.baseUrl}${blogPath}/${post.slug}`,
    })
  } else {
    items.push({
      '@type': 'ListItem',
      position: 3,
      name: post.title,
      item: `${config.baseUrl}${blogPath}/${post.slug}`,
    })
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
}
